import React from 'react';
import { Grid, Box, Typography } from '@mui/material';
import TextInput from '../../../sharedModules/form/components/TextInput';
import InspectionRadioGroup from './InspectionRadioGroup';
import AgeSelect from './AgeSelect';
import ManufacturerSelect from './ManufacturerSelect';
import { resetFieldsForSystemType, resetFieldsForSubtype } from './hvacSystemFieldRules';
import {
  conditionOptions,
  conditionWithNaOptions,
  yesNoOptions,
  hvacAgeOptions,
  hvacSystemTypeOptions,
  splitSystemSubtypeOptions,
  SPLIT_SYSTEM_DUAL_SUBTYPES,
  HVAC_AGE_COLOR_MAP,
  HVAC_AGE_CONDITION_MAP,
} from '../config/options';
import { subHeaderSx } from './inspectionReportForm/inspectionReportFormStyles';

export interface HvacSystemValues {
  id: string;
  location: string;
  manufacturer: string;
  age: string;
  ageCondition: string;
  systemType: string;
  splitSystemSubtype: string;
  indoorManufacturer: string;
  outdoorManufacturer: string;
  indoorAge: string;
  indoorAgeCondition: string;
  outdoorAge: string;
  outdoorAgeCondition: string;
  overallVisualCondition: string;
  overallOperation: string;
  properClearance: string;
  serviceRecordUpdated: string;
  notes: string;
  // Boiler-specific fields (only used when systemType is 'boiler')
  boilerAcPresent: string;
  boilerExpansionTankCondition: string;
  boilerHeatExchangerCondition: string;
  boilerPressureReliefCondition: string;
  boilerAutoFillCondition: string;
}

export function createDefaultHvacSystem(): HvacSystemValues {
  return {
    id: crypto.randomUUID(),
    location: '',
    manufacturer: '',
    age: '',
    ageCondition: '',
    systemType: '',
    splitSystemSubtype: '',
    indoorManufacturer: '',
    outdoorManufacturer: '',
    indoorAge: '',
    indoorAgeCondition: '',
    outdoorAge: '',
    outdoorAgeCondition: '',
    overallVisualCondition: '',
    overallOperation: '',
    properClearance: '',
    serviceRecordUpdated: '',
    notes: '',
    boilerAcPresent: '',
    boilerExpansionTankCondition: '',
    boilerHeatExchangerCondition: '',
    boilerPressureReliefCondition: '',
    boilerAutoFillCondition: '',
  };
}

interface HvacSystemFieldsProps {
  values: HvacSystemValues;
  onChange: (field: keyof HvacSystemValues, value: any) => void;
  manufacturers: { value: string; label: string }[];
}

type QuestionField = {
  [K in keyof HvacSystemValues]: HvacSystemValues[K] extends string ? K : never;
}[keyof HvacSystemValues];

interface Question {
  label: string;
  field: QuestionField;
  options: typeof yesNoOptions;
}

const BOILER_QUESTIONS: Question[] = [
  { label: 'Air Conditioning', field: 'boilerAcPresent', options: yesNoOptions },
  { label: 'Expansion Tank', field: 'boilerExpansionTankCondition', options: conditionWithNaOptions },
  { label: 'Heat Exchanger', field: 'boilerHeatExchangerCondition', options: conditionWithNaOptions },
  { label: 'Pressure Relief', field: 'boilerPressureReliefCondition', options: conditionWithNaOptions },
  { label: 'Auto-Fill', field: 'boilerAutoFillCondition', options: conditionWithNaOptions },
];

const COMMON_QUESTIONS: Question[] = [
  { label: 'Overall/Visual Condition', field: 'overallVisualCondition', options: conditionOptions },
  { label: 'Overall Operation', field: 'overallOperation', options: conditionOptions },
  { label: 'Proper Clearance Around Equipment', field: 'properClearance', options: yesNoOptions },
  { label: 'Service Record Updated', field: 'serviceRecordUpdated', options: yesNoOptions },
];

const splitSectionSx = {
  bgcolor: '#f0f5fa',
  border: '1.5px solid #c8d8e8',
  borderRadius: 2,
  p: 2,
  mb: 2,
};

const HvacSystemFields: React.FC<HvacSystemFieldsProps> = ({ values, onChange, manufacturers }) => {
  const isSplitSystem = values.systemType === 'split_system';
  const isDualSubtype = SPLIT_SYSTEM_DUAL_SUBTYPES.has(values.splitSystemSubtype);

  const applyResets = (resets: Partial<HvacSystemValues>) => {
    (Object.keys(resets) as (keyof HvacSystemValues)[]).forEach((field) => onChange(field, resets[field]));
  };

  const handleSystemTypeChange = (val: string) => {
    onChange('systemType', val);
    applyResets(resetFieldsForSystemType(val));
  };

  const handleSubtypeChange = (val: string) => {
    onChange('splitSystemSubtype', val);
    applyResets(resetFieldsForSubtype(values, val));
  };

  // One unit (indoor or outdoor) of a dual split system: heading + Manufacturer + Age.
  const renderUnitFields = (
    unit: 'Indoor' | 'Outdoor',
    color: string,
    manufacturerField: 'indoorManufacturer' | 'outdoorManufacturer',
    ageField: 'indoorAge' | 'outdoorAge',
    conditionField: 'indoorAgeCondition' | 'outdoorAgeCondition',
    headingMarginTop?: number,
  ) => (
    <>
      <Grid item xs={12} sx={{ mt: headingMarginTop }}>
        <Typography
          sx={{
            fontSize: '10px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color,
            mb: 0.75,
          }}
        >
          {unit} Unit
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <ManufacturerSelect
          value={values[manufacturerField]}
          onChange={(val) => onChange(manufacturerField, val)}
          options={manufacturers}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <AgeSelect
          value={values[ageField]}
          onChange={(val, condition) => {
            onChange(ageField, val);
            onChange(conditionField, condition);
          }}
          options={hvacAgeOptions}
          colorMap={HVAC_AGE_COLOR_MAP}
          conditionMap={HVAC_AGE_CONDITION_MAP}
          label={`${unit} Age`}
        />
      </Grid>
    </>
  );

  const renderSingleUnitFields = (sx?: { mb: number }) => (
    <Grid container spacing={2} sx={sx}>
      <Grid item xs={12} md={6}>
        <ManufacturerSelect
          value={values.manufacturer}
          onChange={(val) => onChange('manufacturer', val)}
          options={manufacturers}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <AgeSelect
          value={values.age}
          onChange={(val, condition) => {
            onChange('age', val);
            onChange('ageCondition', condition);
          }}
          options={hvacAgeOptions}
          colorMap={HVAC_AGE_COLOR_MAP}
          conditionMap={HVAC_AGE_CONDITION_MAP}
        />
      </Grid>
    </Grid>
  );

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <TextInput label="Location" value={values.location} valueChanged={(val) => onChange('location', val)} />
        </Grid>
      </Grid>

      <Typography sx={subHeaderSx}>Type*</Typography>
      <Box sx={{ mb: 2 }}>
        <InspectionRadioGroup
          radioOptions={hvacSystemTypeOptions}
          value={values.systemType}
          setValue={handleSystemTypeChange}
        />
      </Box>

      {!isSplitSystem && renderSingleUnitFields({ mb: 2 })}

      {isSplitSystem && (
        <Box sx={splitSectionSx}>
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 700,
              color: '#3d5a80',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 1.5,
            }}
          >
            Split System Configuration
          </Typography>

          <Typography sx={subHeaderSx}>Sub-type</Typography>
          <Box sx={{ mb: 2 }}>
            <InspectionRadioGroup
              radioOptions={splitSystemSubtypeOptions}
              value={values.splitSystemSubtype}
              setValue={handleSubtypeChange}
            />
          </Box>

          {isDualSubtype ? (
            <Grid container spacing={2}>
              {renderUnitFields('Indoor', '#1565c0', 'indoorManufacturer', 'indoorAge', 'indoorAgeCondition')}
              {renderUnitFields('Outdoor', '#e65100', 'outdoorManufacturer', 'outdoorAge', 'outdoorAgeCondition', 1)}
            </Grid>
          ) : (
            values.splitSystemSubtype && renderSingleUnitFields()
          )}
        </Box>
      )}

      <Typography sx={subHeaderSx}>Inspection Details</Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[...(values.systemType === 'boiler' ? BOILER_QUESTIONS : []), ...COMMON_QUESTIONS].map(
          ({ label, field, options }) => (
            <Grid item xs={12} key={field}>
              <InspectionRadioGroup
                label={label}
                radioOptions={options}
                value={values[field]}
                setValue={(val) => onChange(field, val)}
              />
            </Grid>
          ),
        )}
      </Grid>

      <TextInput
        label="HVAC Notes"
        value={values.notes}
        valueChanged={(val) => onChange('notes', val)}
        multiline
        rows={3}
      />
    </Box>
  );
};

export default HvacSystemFields;
